import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { PageMeta, PageHero, Section, SectionHeading, FeatureCard, CtaBlock, Pill } from "@/components/PageShellParts";
import { AlertTriangle, BarChart3, Briefcase, ClipboardList, Compass, HeartPulse, Lock, ShieldAlert, Sparkles, TrendingDown, Users } from "lucide-react";

const Icons = { AlertTriangle, BarChart3, Briefcase, ClipboardList, Compass, HeartPulse, Lock, ShieldAlert, Sparkles, TrendingDown, Users };

export default function ForCompaniesPage() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <PageMeta title="For Companies" description="Structured, privacy-aware wellbeing insights for organizations and employee support programs." />
      <div className="pt-20">
      <PageHero
        eyebrow="For Companies"
        title="Structured Wellbeing Insights for Organizations"
        subtitle="PsyTech helps organizations explore privacy-aware, AI-assisted wellbeing assessment workflows while keeping human oversight and ethical boundaries clear."
      >
        <div className="flex flex-wrap gap-2">
          <Pill tone="cyan">Privacy-aware</Pill>
          <Pill tone="cyan">Anonymized where appropriate</Pill>
          <Pill tone="cyan">Supports wellbeing teams</Pill>
        </div>
      </PageHero>

      <Section>
        <SectionHeading eyebrow="Pain points" title="What HR and wellbeing teams commonly face" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.HeartPulse} title="Stress and burnout signals">Teams often see the effects late — when individuals are already at risk.</FeatureCard>
          <FeatureCard icon={Icons.Compass} title="Limited wellbeing visibility">Hard to get a meaningful sense of how a team is doing without being intrusive.</FeatureCard>
          <FeatureCard icon={Icons.ClipboardList} title="Fragmented support processes">Check-ins, referrals, and follow-ups exist in different tools and owners.</FeatureCard>
          <FeatureCard icon={Icons.Lock} title="Need for privacy-sensitive tools">Employees rightfully expect wellbeing tools to respect their privacy.</FeatureCard>
          <FeatureCard icon={Icons.BarChart3} title="Hard to measure program impact">Ad-hoc processes make it difficult to evaluate wellbeing investments.</FeatureCard>
          <FeatureCard icon={Icons.TrendingDown} title="Inconsistent touch-points">Support experience varies widely between teams or locations.</FeatureCard>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="How PsyTech helps" title="Structured check-ins with clear guardrails" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard icon={Icons.ClipboardList} title="Structured wellbeing check-ins">Clear, consistent questionnaires designed for employee context.</FeatureCard>
          <FeatureCard icon={Icons.Sparkles} title="AI-assisted summaries">Structured summaries that help wellbeing teams prioritize follow-ups.</FeatureCard>
          <FeatureCard icon={Icons.Users} title="Clear referral/support pathways">Make it easy to route people to appropriate support.</FeatureCard>
          <FeatureCard icon={Icons.Lock} title="Privacy-aware reporting">Outputs designed with sensitivity and access control in mind.</FeatureCard>
          <FeatureCard icon={Icons.BarChart3} title="Anonymized group-level insights">Where appropriate, trends at team or organization level — without identifying individuals.</FeatureCard>
          <FeatureCard icon={Icons.Briefcase} title="Supports professional oversight">Occupational health and wellbeing professionals stay at the centre of decisions.</FeatureCard>
        </div>
      </Section>

      <Section>
        <div className="max-w-3xl bg-rose-50 border border-rose-200 rounded-2xl p-6 flex items-start gap-4">
          <ShieldAlert className="w-6 h-6 text-rose-700 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2 font-['Plus_Jakarta_Sans']">Strict ethical boundary</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              PsyTech should not be used for punitive HR decisions, employee surveillance, or individual performance evaluation.
              The platform is designed to support employee wellbeing — never to replace, monitor, or discipline employees.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Boundaries" title="What PsyTech does not do" />
        <div className="grid md:grid-cols-2 gap-3">
          {[
            "No diagnosis",
            "No employment decision automation",
            "No replacement for occupational health professionals",
            "No emergency care",
          ].map((t) => (
            <div key={t} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{t}</span>
            </div>
          ))}
        </div>
      </Section>

      <CtaBlock
        title="Explore a workplace wellbeing pilot"
        subtitle="We'll scope a pilot together that respects employee privacy, your wellbeing team's role, and your organization's values."
        primaryHref="/pilot-program"
        primaryLabel="Explore the pilot program"
        secondaryHref="/#contact"
        secondaryLabel="Contact PsyTech"
        testid="for-companies-cta"
      />
    </div>
      <div className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 text-center">
          <p className="text-xs text-slate-500 leading-relaxed">
            <strong className="text-slate-700">Important:</strong> PsyTech provides digital assessment support and educational information only.
            It does not provide diagnosis, treatment, medication advice, or emergency support.
            AI-generated insights should be reviewed by qualified professionals before clinical use.
          </p>
        </div>
      </div>
      <Footer />
    </main>
  );
}
